import type { Product } from '../lib/database.types';
import { formatPrice } from '../lib/cart';

/**
 * Section « Les incontournables / Nos best-sellers » — portée À L'IDENTIQUE du legacy.
 * Produits mis en avant = `is_featured` (la base fait foi). Masquée si aucun.
 * Carte : grande photo (ou dégradé de marque) + badge POPULAIRE + nom + desc + prix + Ajouter.
 */
interface BestSellersProps {
  products: Product[];
  currency: 'EUR' | 'CHF';
  onSelect: (p: Product) => void;
  onSeeAll: () => void;
}

export default function BestSellers({ products, currency, onSelect, onSeeAll }: BestSellersProps) {
  if (products.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 lg:px-6 lg:py-24">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div
            className="mb-2 text-sm font-bold uppercase tracking-widest"
            style={{ color: 'var(--brand-primary)' }}
          >
            Les incontournables
          </div>
          <h2 className="display-font text-4xl font-black lg:text-6xl">Nos best-sellers</h2>
          <p className="mt-3 max-w-xl text-gray-600">
            Les plus commandés ce mois-ci. Cliquez pour personnaliser.
          </p>
        </div>
        <button
          onClick={onSeeAll}
          className="underline-grow hidden text-sm font-bold sm:block"
          style={{ color: 'var(--brand-primary)' }}
        >
          Voir toute la carte →
        </button>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <div
            key={p.id}
            className="group overflow-hidden rounded-2xl bg-white shadow-md transition-all hover:-translate-y-1 hover:shadow-2xl"
          >
            <button onClick={() => onSelect(p)} className="block w-full text-left">
              <div
                className="relative flex h-56 items-center justify-center overflow-hidden text-8xl lg:h-64"
                style={{
                  background:
                    'linear-gradient(135deg, color-mix(in srgb, var(--brand-primary) 10%, white), color-mix(in srgb, var(--brand-primary) 22%, white))',
                }}
              >
                <span className="transition-transform duration-500 group-hover:scale-110">{p.emoji || '🍽️'}</span>
                {p.image_url && (
                  <img
                    src={p.image_url}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div className="absolute left-3 top-3 rounded-full bg-yellow-400 px-3 py-1 text-xs font-extrabold text-yellow-900 shadow">
                  ★ POPULAIRE
                </div>
              </div>
            </button>
            <div className="p-5">
              <h3 className="text-xl font-extrabold">{p.name}</h3>
              {p.description && (
                <p className="mt-1 line-clamp-2 min-h-[40px] text-sm text-gray-600">
                  {p.description}
                </p>
              )}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-2xl font-extrabold" style={{ color: 'var(--brand-primary)' }}>
                  {formatPrice(p.base_price_cents, currency)}
                </div>
                <button
                  onClick={() => onSelect(p)}
                  className="flex items-center gap-1.5 rounded-xl px-4 py-2.5 font-bold text-white transition active:scale-95"
                  style={{ background: 'var(--brand-primary)' }}
                >
                  <span className="text-base">+</span>
                  <span className="text-sm">Ajouter</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onSeeAll}
        className="mt-8 w-full rounded-xl border-2 py-3 text-center font-bold sm:hidden"
        style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}
      >
        Voir toute la carte →
      </button>
    </section>
  );
}
